import { cva } from 'class-variance-authority';
import * as React from 'react';
import {
	SelectionCardAlignmentEnum,
	SelectionCardSizeEnum,
	SelectionCardVariantEnum,
	type ISelectionCardProps,
} from './selection-card.type';
import { Text } from '@components/atoms';
import { cn } from '@utils';

const selectionCardVariants = cva(
	'group flex cursor-pointer appearance-none flex-col justify-center overflow-hidden transition-all duration-200 focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:outline-none',
	{
		variants: {
			size: {
				sm: 'min-h-[10rem] w-[12rem] p-3',
				md: 'min-h-[12.1rem] w-[15.2rem] p-4',
				lg: 'min-h-[14rem] w-[18rem] p-5',
			},
			variant: {
				default: 'border border-borderPrimary bg-bgPrimary text-primary',
				outlined: 'border border-borderPrimary bg-white text-primary shadow-sm',
			},
			isSelected: {
				true: 'border-primary bg-bgFields ring-1 ring-primary',
				false: '',
			},
			disabled: {
				true: 'pointer-events-none cursor-not-allowed opacity-50',
				false: '',
			},
			hasDescription: {
				true: 'min-h-[15rem] w-[20rem] gap-4 rounded-xl',
				false: 'gap-2 rounded-lg',
			},
			alignment: {
				left: 'items-start text-left',
				center: 'items-center text-center',
				right: 'items-end text-right',
			},
		},
		compoundVariants: [
			{
				variant: 'default',
				isSelected: false,
				class: 'hover:border-primary/50 hover:bg-bgFields',
			},
			{
				variant: 'outlined',
				isSelected: false,
				class: 'hover:border-primary hover:bg-bgFields hover:shadow-md',
			},
		],
		defaultVariants: {
			size: 'md',
			variant: 'default',
			isSelected: false,
			disabled: false,
			hasDescription: false,
			alignment: 'center',
		},
	}
);

const contentAlignmentClass: Record<SelectionCardAlignmentEnum, string> = {
	[SelectionCardAlignmentEnum.LEFT]: 'w-full items-start text-left',
	[SelectionCardAlignmentEnum.CENTER]: 'w-full items-center text-center',
	[SelectionCardAlignmentEnum.RIGHT]: 'w-full items-end text-right',
};

const iconContainerVariants = cva('flex shrink-0 items-center justify-center rounded-xl transition-colors', {
	variants: {
		size: {
			sm: 'h-10 w-10 text-xl',
			md: 'h-[5.8rem] w-[5.8rem] text-2xl',
			lg: 'h-[7rem] w-[7rem] text-3xl',
		},
		isSelected: {
			true: 'bg-primary text-white',
			false: 'bg-bgFields text-primary group-hover:bg-primary/10',
		},
	},
	defaultVariants: {
		size: 'md',
		isSelected: false,
	},
});

const SelectionCard = React.forwardRef<HTMLButtonElement, ISelectionCardProps>(
	(
		{
			label,
			description,
			icon,
			onClick,
			size = SelectionCardSizeEnum.MD,
			variant = SelectionCardVariantEnum.DEFAULT,
			alignment,
			isSelected = false,
			disabled = false,
			className,
			testId,
			type = 'button',
			...rest
		},
		ref
	) => {
		const hasDescription = Boolean(description);
		const resolvedAlignment =
			alignment ?? (hasDescription ? SelectionCardAlignmentEnum.LEFT : SelectionCardAlignmentEnum.CENTER);
		const labelSize = size === SelectionCardSizeEnum.LG ? 'lg' : size === SelectionCardSizeEnum.SM ? 'sm' : 'md';

		return (
			<button
				ref={ref}
				{...rest}
				type={type}
				onClick={onClick}
				disabled={disabled}
				className={cn(
					selectionCardVariants({ size, variant, isSelected, disabled, hasDescription, alignment: resolvedAlignment }),
					className
				)}
				data-testid={testId ?? 'selection-card'}
				aria-pressed={isSelected}
			>
				{icon && <span className={iconContainerVariants({ size, isSelected })}>{icon}</span>}
				<span className={cn('flex min-w-0 flex-col', hasDescription && 'gap-1', contentAlignmentClass[resolvedAlignment])}>
					<Text size={labelSize} weight="bold" color="primary" className="w-full leading-tight break-words">
						{label}
					</Text>
					{description && (
						<Text size="sm" color="mediumGray" className="w-full leading-6 break-words">
							{description}
						</Text>
					)}
				</span>
			</button>
		);
	}
);
SelectionCard.displayName = 'SelectionCard';

export { SelectionCard, selectionCardVariants };
